import { AnyAction, createAsyncThunk, ThunkDispatch } from '@reduxjs/toolkit';

import ERRORS from '~/constants/errors';

import { selectBusinessDetails, selectIsPropertyOwner } from '../selectors/selectBusinessDetails';
import { refDataApi } from '../services/refData';
import {
  BusinessDetails,
  updateIndustryOnly,
  updateOccupation,
  updateOccupationCategoryOnly,
  updatePropertyOwnerOccupation,
} from '../slices/businessDetails';
import { clearCpiInsured, updateDynamicCoverQuestions } from '../slices/coverDetails';
import { hardDecline } from '../slices/decline';
import { AppDispatch, RootState } from '../store';
import { occupationSearchTypes } from '../types/analytics';
import {
  Industry,
  Occupation,
  occupationListItemToOccupation,
  OccupationsListItem,
  UnderwritingStatus,
} from '../types/refData';
import { utcDateFormatter } from '../utils';

const findOccupationListItem = async <T extends ThunkDispatch<unknown, unknown, AnyAction>>({
  occupationsList,
  anzsic,
  dispatch,
  coverStart,
}: {
  occupationsList?: OccupationsListItem[];
  anzsic: string;
  dispatch: T;
  coverStart: string;
}) => {
  if (occupationsList) {
    return occupationsList.find((o) => o.code === anzsic);
  }
  const occupationListItem = await dispatch(
    refDataApi.endpoints?.getOccupationsList.initiate({
      effectiveDate: utcDateFormatter(coverStart),
      anzsics: [anzsic],
    })
  ).unwrap();
  if (occupationListItem.occupations.length < 1) {
    return undefined;
  }
  return occupationListItem.occupations[0];
};

const updateRefData = createAsyncThunk<
  unknown,
  OccupationsListItem | undefined,
  {
    state: RootState;
  }
>('updateRefData', async (occupationListItem, { getState, dispatch }) => {
  const { industry, coverStart, occupationCategory, occupation } =
    selectBusinessDetails(getState());
  if (!coverStart) throw new Error('Cover start date not found');
  if (!occupation) return;
  const coverStartFormatted = utcDateFormatter(coverStart);
  dispatch(clearCpiInsured());

  const doUpdateDynamicCoverQuestions = async () => {
    const dynamicCoverQuestionsRequest = await dispatch(
      refDataApi.endpoints?.getDyanmicCoverQuestions.initiate({
        anzsicCode: occupation.anzsicCode,
        effectiveDate: utcDateFormatter(coverStart),
      })
    ).unwrap();
    dispatch(updateDynamicCoverQuestions(dynamicCoverQuestionsRequest.covers));
  };

  const doUpdateIndustry = async () => {
    if (!occupationListItem) return;
    let industryNew: Industry | undefined;
    if (!industry || industry.industryCode !== occupationListItem.industryCode) {
      const industryResponse = await dispatch(
        refDataApi.endpoints?.getIndustries.initiate({ effectiveDate: coverStartFormatted })
      ).unwrap();
      industryNew = industryResponse.industries.find(
        (i) => i.industryCode === occupationListItem.industryCode
      )!;
      dispatch(updateIndustryOnly(industryNew));
    }

    const industryCode = industryNew?.industryCode ?? industry?.industryCode;
    if (
      (!occupationCategory ||
        occupationCategory.categoryCode !== occupationListItem.categoryCode) &&
      industryCode
    ) {
      const occupationResponse = await dispatch(
        refDataApi.endpoints?.getOccupationCategories.initiate({
          effectiveDate: coverStartFormatted,
          industryCode,
        })
      ).unwrap();
      dispatch(
        updateOccupationCategoryOnly(
          occupationResponse.occupationCategories.find(
            (o) => o.categoryCode === occupationListItem.categoryCode
          )
        )
      );
    }
  };
  await Promise.all([doUpdateIndustry(), doUpdateDynamicCoverQuestions()]);
});

const getOccupation = async ({
  anzsic,
  businessDetails,
  state,
  dispatch,
}: {
  anzsic: string;
  businessDetails: Partial<BusinessDetails>;
  state: RootState;
  dispatch: AppDispatch;
}) => {
  const { industry, occupationCategory, coverStart, occupationsList } = businessDetails;
  if (!coverStart) throw new Error('Cover start date not found');

  let selectedOccupation: Occupation | undefined;
  if (industry && occupationCategory && coverStart) {
    const selectOccupations = refDataApi.endpoints?.getOccupations.select({
      effectiveDate: utcDateFormatter(coverStart),
      occupationCategory: occupationCategory.categoryCode,
      industryCode: industry.industryCode,
    })(state);
    if (selectOccupations.isSuccess) {
      selectedOccupation = selectOccupations.data.occupations.find((o) => o.anzsicCode === anzsic);
    }
  }

  let occupationListItem: OccupationsListItem | undefined;
  if (occupationsList) {
    occupationListItem = occupationsList.find((o) => o.code === anzsic);
    if (occupationListItem) selectedOccupation = occupationListItemToOccupation(occupationListItem);
  }

  if (!selectedOccupation) {
    const occupationItem = await findOccupationListItem({
      occupationsList,
      anzsic,
      dispatch,
      coverStart,
    });

    if (!occupationItem) {
      throw new Error(`Occupation ${anzsic} not found`);
    }
    selectedOccupation = occupationListItemToOccupation(occupationItem);
  }

  if (!selectedOccupation) {
    throw new Error('Occupation not found');
  }
  return {
    selectedOccupation,
    occupationListItem,
  };
};

export const selectOccupationAction = createAsyncThunk<
  Occupation,
  {
    anzsic: string;
    searchType?: occupationSearchTypes;
  },
  {
    state: RootState;
    dispatch: AppDispatch;
  }
>('selectOccupation', async ({ anzsic, searchType }, { getState, dispatch }) => {
  const businessDetails = selectBusinessDetails(getState());
  const { coverStart, occupationsList, occupation } = businessDetails;
  if (occupation?.anzsicCode === anzsic) return occupation;
  if (!coverStart) throw new Error('Cover start date not found');

  const { selectedOccupation, occupationListItem } = await getOccupation({
    anzsic,
    businessDetails,
    state: getState(),
    dispatch,
  });
  dispatch(updateOccupation(selectedOccupation));

  if (selectIsPropertyOwner(getState()) && selectedOccupation?.propertyOwnerAnzsic) {
    const propertyOwnerOccupation = await findOccupationListItem({
      occupationsList,
      anzsic: selectedOccupation.propertyOwnerAnzsic,
      dispatch,
      coverStart,
    });
    if (!propertyOwnerOccupation) {
      throw new Error(
        `Property owner occupation ${selectedOccupation?.propertyOwnerAnzsic} not found`
      );
    }
    dispatch(updatePropertyOwnerOccupation(propertyOwnerOccupation));
    if (propertyOwnerOccupation.uw_status === UnderwritingStatus.Decline) {
      dispatch(
        hardDecline({
          ...ERRORS.occupation,
          searchType,
        })
      );
    }
  } else if (selectedOccupation.underwritingStatus === UnderwritingStatus.Decline) {
    dispatch(
      hardDecline({
        ...ERRORS.occupation,
        searchType,
      })
    );
  }
  await dispatch(updateRefData(occupationListItem));
  return selectedOccupation;
});
